import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import './LegalPage.css';

// One layout for every public text page (privacy now; about and cookies the same way). The words are data (content/legal/*.js), so a page
// can be offered in another language without touching this file. The look is the landing page's own (.lp), plainly set.
const LINKS = [
  ['/legal/privacy', 'Privatliv'],
  ['/legal/cookies', 'Cookies'],
];

// `**word**` becomes bold; nothing else is interpreted, and nothing is inserted as HTML.
function Inline({ text }) {
  return String(text).split('**').map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>));
}

function Block({ block }) {
  if (block.p) return <p><Inline text={block.p} /></p>;
  if (block.ul) return <ul>{block.ul.map((t, i) => <li key={i}><Inline text={t} /></li>)}</ul>;
  if (block.note) return <div className="lgp-box"><Inline text={block.note} /></div>;
  if (block.table) {
    return (
      <div className="lgp-tw">
        <table className="lgp-t">
          <thead><tr>{block.table.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{block.table.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} data-label={block.table.head[j]}><Inline text={cell} /></td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  return null;
}

const LOGO = (
  <svg viewBox="0 0 100 100" fill="none" aria-hidden="true">
    <defs><linearGradient id="lgp-g" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#4EA8F5" /><stop offset="55%" stopColor="#6C63F2" /><stop offset="100%" stopColor="#B44FE0" /></linearGradient></defs>
    <path d="M78,16 L18,16 L18,84 L78,84" stroke="url(#lgp-g)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
    <polygon points="32,28 40,28 40,72 32,72" fill="url(#lgp-g)" /><polygon points="60,28 68,28 68,72 60,72" fill="url(#lgp-g)" /><polygon points="32,28 40,28 68,72 60,72" fill="url(#lgp-g)" />
  </svg>
);

export default function LegalPage({ content }) {
  const [active, setActive] = useState(content.sections[0]?.id);
  const ids = useMemo(() => content.sections.map((s) => s.id), [content]);

  useEffect(() => {
    document.title = `${content.title} · NordComply`;
    window.scrollTo(0, 0);
    // The page is a document, not the app: it sits on the paper colour like the landing page.
    const prev = document.body.style.background;
    document.body.style.background = '#F7F4EE';
    return () => { document.body.style.background = prev; };
  }, [content]);

  // The contents list follows the reader.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const seen = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
      const first = ids.find((id) => seen.get(id));
      if (first) setActive(first);
    }, { rootMargin: '-10% 0px -70% 0px' });
    ids.forEach((id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [ids]);

  return (
    <div className="lp lgp">
      <header className="lgp-nav">
        <a className="lp-logo" href="/">{LOGO}<span>nord<i>comply</i></span></a>
        <span className="lp-navr">
          <a href="/">Forside</a>
          {LINKS.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
          <a href="/login">Log ind</a>
        </span>
      </header>

      <main>
        <div className="lgp-hero">
          <span className="lp-kicker">{content.kicker}</span>
          <h1 className="lgp-h1">{content.title}</h1>
          <p className="lgp-lead">{content.lead}</p>
          {(content.updated || content.scope) && <div className="lgp-meta">{content.updated && <span>{content.updated}</span>}{content.scope && <span>{content.scope}</span>}</div>}
        </div>

        <div className={`lgp-grid${content.toc === false ? ' lgp-grid--one' : ''}`}>
          {content.toc !== false && (
            <nav className="lgp-toc" aria-label={content.tocTitle}>
              <b>{content.tocTitle}</b>
              {content.sections.map((s) => <a key={s.id} href={`#${s.id}`} className={active === s.id ? 'on' : ''}>{s.title}</a>)}
            </nav>
          )}
          <div>
            {content.summary && (
              <div className="lgp-sk">
                <b>{content.summaryTitle}</b>
                <ul>{content.summary.map((t, i) => <li key={i}><Inline text={t} /></li>)}</ul>
              </div>
            )}
            {content.sections.map((s) => (
              <section key={s.id} id={s.id} className="lgp-s">
                <h2>{s.title}</h2>
                {s.blocks.map((b, i) => <Block key={i} block={b} />)}
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer className="lgp-foot">
        <span>nordcomply · Indsigt. Forståelse. Handling. · support@nordcomply.dk</span>
        <span>{LINKS.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}<a href="/login">Log ind</a></span>
      </footer>
    </div>
  );
}
